import { Icon } from '@mairistem/composants';
import type { IndicateursCleType, RecuStatsType } from '@/types';
import { formatMonetaire } from '@/utils';
import TileBase from './TileBase';
import TileHeader from './TileHeader';

type ChiffreItemPropsType = {
  label: string;
  shortLabel?: string;
  montant: number;
  nombre: number | string;
  icon: string;
  red?: boolean;
  disabled?: boolean;
  heureInterrogation?: string | null;
};

const ChiffreItem = ({
  label,
  shortLabel,
  montant,
  nombre,
  icon,
  red = false,
  disabled = false,
  heureInterrogation,
}: ChiffreItemPropsType) => {
  // Détermine la couleur de l'icône en fonction de l'état
  const getIconColor = () => {
    if (disabled) return '#999';
    if (red) return '#db2828';
    return '#66bb6a';
  };

  return (
    <div className={`chiffre-jour-box ${red ? 'red' : ''} ${disabled ? 'disabled' : ''}`}>
      <Icon
        // @ts-expect-error
        name={icon}
        iconSet="Lucide"
        className="chiffre-jour-icon"
        style={{ stroke: getIconColor() }}
      />
      <div className="chiffre-jour-content">
        <div className="chiffre-jour-header">
          <span className="chiffre-jour-label">
            <span className="label-full">{label}</span>
            {shortLabel && <span className="label-short">{shortLabel}</span>}
          </span>
          {!disabled && (
            <span className="chiffre-jour-montant">{formatMonetaire(montant, 2, true)}</span>
          )}
        </div>
        <div className="chiffre-jour-nombre-row">
          <span className="chiffre-jour-nombre">
            {typeof nombre === 'number' ? `${nombre} reçu${nombre > 1 ? 's' : ''}` : nombre}
          </span>
          {heureInterrogation && !disabled && (
            <span className="chiffre-jour-heure-mfo" title="Heure d'actualisation">
              <Icon
                name="Clock"
                iconSet="Lucide"
                className="chiffre-jour-heure-mfo-icon"
                size={14}
              />
              {heureInterrogation}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

type TileChiffresJourPropsType = {
  indicateurs: IndicateursCleType | null;
  recuStats: RecuStatsType;
  hasPaiementInternet: boolean;
  hasPrelevementInternet: boolean;
  heureInterrogationMfo?: string | null;
  loading?: boolean;
};

const TileChiffresJour = ({
  indicateurs,
  recuStats,
  hasPaiementInternet,
  hasPrelevementInternet,
  heureInterrogationMfo,
  loading,
}: TileChiffresJourPropsType) => (
  <TileBase className="tile-chiffres-jour" loading={loading}>
    <TileHeader title="Les chiffres du jour" />

    <div className="chiffres-jour-grid">
      <ChiffreItem
        icon="CircleCheck"
        label="Encaissements"
        montant={indicateurs?.mesEncaissementsDuJour || 0}
        nombre={recuStats.nombreEncaissements}
      />
      <ChiffreItem
        icon="CreditCard"
        label="Cartes bancaires internet"
        shortLabel="CBI"
        montant={indicateurs?.cartesBancairesInternetDuJour || 0}
        nombre={hasPaiementInternet ? recuStats.nombreCBI : 'Mode de règlement inactif'}
        disabled={!hasPaiementInternet}
        heureInterrogation={heureInterrogationMfo}
      />
      <ChiffreItem
        icon="CircleX"
        label="Annulations"
        montant={indicateurs?.mesAnnulationsDuJour || 0}
        nombre={recuStats.nombreAnnulations}
        red
      />
      <ChiffreItem
        icon="ArrowDownToLine"
        label="Prélèvements internet"
        shortLabel="PRI"
        montant={indicateurs?.prelevementsInternetDuJour || 0}
        nombre={hasPrelevementInternet ? recuStats.nombrePRI : 'Mode de règlement inactif'}
        disabled={!hasPrelevementInternet}
        heureInterrogation={heureInterrogationMfo}
      />
    </div>
  </TileBase>
);

export default TileChiffresJour;
